import { useState } from "react";
import { useSearchParams, Outlet } from "react-router-dom";

import { DiscoveryModeContext } from "@/contexts/DiscoveryModeContext";
import { Box, Breadcrumbs, Checkbox, Container, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";

import { Autocomplete } from "@mui/lab";

import { useSearchParam } from "@/hooks";

import i18n from '@/i18n'

import client from '@/client'
import { gql } from "@apollo/client";

const BREAKDOWN_TYPES = [
  'track',
  'label',
  'organization'
]


const STAGES = [
  {
    id: 'MID',
    name: 'mid-campaign'
   },
   {
    id: 'END',
    name: 'end-of-campaign'
   }
]


export function DiscoveryModeLayout() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [labelQuery, setLabelQuery] = useState('')
  const [organizationQuery, setOrganizationQuery] = useState('')
  const [artistQuery, setArtistQuery] = useState('')
  const [artists, setArtists] = useState([])
  const [labels, setLabels] = useState([])
  const [organizations, setOrganizations] = useState([])
  const [selectedOrganizations, setSelectedOrganizations] = useSearchParam('organization', undefined, 'json',  true)
  const [end, setEnd] = useSearchParam('end', undefined, 'date', false)
  const [start, setStart] = useSearchParam('start', undefined, 'date', false)
  const [selectedLabels, setSelectedLabels] = useSearchParam('label', undefined, 'json', true)
  const [selectedArtists, setSelectedArtists] = useSearchParam('artist', undefined, 'json', true)
  const [selectedRecordings, setSelectedRecordings] = useSearchParam('recording', undefined, 'json', true)
  const [breakdown, setBreakdown] = useSearchParam('breakdown', 'track')
  const [endDataAvailable, setEndDataAvailable] = useState(false)
  const [midDataAvailable, setMidDataAvailable] = useState(false)
  const [stage, setStage] = useSearchParam('stage', endDataAvailable ? 'END' : 'MID')
  
  return (
    <DiscoveryModeContext.Provider
      value={{
        selectedOrganizations,
        setSelectedOrganizations,
        selectedLabels,
        setSelectedLabels,
        selectedArtists,
        setSelectedArtists,
        selectedRecordings,
        setSelectedRecordings,
        artists,
        setArtists,
        labels,
        setLabels,
        organizations,
        start,
        end,
        setOrganizations,
        breakdown,
        setBreakdown,
        stage,
        setStage,
        midDataAvailable,
        setMidDataAvailable,
        endDataAvailable,
        setEndDataAvailable
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ padding: 1, display: 'flex', flexDirection: "row", width: "100%", alignItems: "center", gap: 1 }}>
          <Typography>{i18n.t('filter-by')}</Typography>
          <FormControl sx={{ minWidth: 220 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              filterOptions={n => n}
              autoComplete
              options={organizations ?? []}
              defaultValue={selectedOrganizations ?? []}
              getOptionLabel={(option) => option.name}
              onInputChange={(event, newInputValue) => {
                client.query({
                  query: gql`
                    query getOrganizations(
                      $q: String
                    ) {
                      studio { 
                        organizations(q: $q) {
                          name
                          id
                          slug
                        }
                      }  
                    }
                  `,
                  variables: {
                    q: newInputValue
                  }
                }).then(({ data }) => {
                  setOrganizations(data.studio.organizations?.slice(0, 10))
                })
              }}
              onChange={(event, values) => {
                setSelectedOrganizations(values)
              }}
              renderOption={(props, s, { selected }) => (
                <li
                  {...props}
                >
                  <Checkbox sx={{ pointerEvents: 'none' }} checked={selected} />
                  {i18n.t(s.name)}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label={i18n.t('organization')} />
              )}
            />
          </FormControl>
          <FormControl sx={{ minWidth: 220 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              filterOptions={n => n}
              options={labels ?? []}
              defaultValue={selectedLabels ?? []}
              getOptionLabel={(option) => option.name}
              onInputChange={(event, newInputValue) => {
                if (selectedOrganizations?.length > 0) {
                  client.query({
                    query: gql`
                      query getLabels(
                        $organizationId: ID!,
                        $q: String
                      ) {
                        studio { 
                          organization(organizationId: $organizationId) {
                            labels(q: $q) {
                              name
                              id
                              slug
                            }
                          }
                        }  
                      }
                    `,
                    variables: {
                      q: newInputValue,
                      organizationId: selectedOrganizations[0].slug
                    }
                  }).then(({ data }) => {
                    setLabels(data.studio.organization.labels.slice(0, 100))
                  })
                } else {
                  client.query({
                    query: gql`
                      query getLabels(
                        $q: String
                      ) {
                        studio { 
                          labels(q: $q) {
                            name
                            id
                            slug
                          }
                        }  
                      }
                    `,
                    variables: {
                      q: newInputValue,
                      organizationId: selectedOrganizations?.[0]?.slug
                    }
                  }).then(({ data }) => {
                    setLabels(data.studio.labels.slice(0, 100))
                  })
                }
              }}
              onChange={(event, values) => {
                setSelectedLabels(values)
              }}
              renderOption={(props, s, { selected }) => (
                <li
                  {...props}
                >
                  <Checkbox sx={{ pointerEvents: 'none' }} checked={selected} />
                  {i18n.t(s.name)}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label={i18n.t('label')} />
              )}
            />
          </FormControl>
          <FormControl sx={{ display: 'none', minWidth: 220 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              filterOptions={n => n}
              options={artists ?? []}
              defaultValue={selectedArtists ?? []}
              getOptionLabel={(option) => option.name}
              onInputChange={(event, newInputValue) => {
                if (selectedOrganizations?.length > 0) {
                  client.query({
                    query: gql`
                      query getArtists(
                        $organizationId: ID!,
                        $q: String
                      ) {
                        studio { 
                          organization(organizationId: $organizationId) {
                            artists(q: $q) {
                              name
                              id
                              slug
                            }
                          }
                        }  
                      }
                    `,
                    variables: {
                      q: newInputValue,
                      organizationId: selectedOrganizations[0].slug
                    }
                  }).then(({ data }) => {
                    setArtists(data.studio.organization.artists.slice(0, 100))
                  })
                } else {
                  client.query({
                    query: gql`
                      query getArtists(
                        $q: String
                      ) {
                        studio { 
                          artists(q: $q) {
                            name
                            id
                            slug
                          }
                        }  
                      }
                    `,
                    variables: {
                      q: newInputValue,
                      organizationId: selectedOrganizations?.[0]?.slug
                    }
                  }).then(({ data }) => {
                    setArtists(data.studio.artists.slice(0, 100))
                  })
                }
              }}
              onChange={(event, values) => {
                setSelectedArtists(values)
              }}
              renderOption={(props, s, { selected }) => (
                <li
                  {...props}
                >
                  <Checkbox sx={{ pointerEvents: 'none' }} checked={selected} />
                  {i18n.t(s.name)}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label={i18n.t('artist')} />
              )}
            />
          </FormControl>
          <FormControl sx={{ display: 'none', minWidth: 220 }}>
            <InputLabel>{i18n.t('breakdown')}</InputLabel>
            <Select
              defaultValue={breakdown}
              onChange={(event) => {
                setBreakdown(event.target.value)
              }}
            >
              {BREAKDOWN_TYPES.map(type => (
                <MenuItem key={type} value={type}>
                  <Typography>{i18n.t(type)}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>{i18n.t('stage')}</InputLabel>
            <Select
              disabled={!endDataAvailable || !midDataAvailable}
              readOnly={!endDataAvailable || !midDataAvailable}
              defaultValue={stage}
              onChange={(event) => {
                setStage(event.target.value)
              }}
            >
              {STAGES.map(stage => (
                <MenuItem key={stage.id} value={stage.id}>
                  <Typography>{i18n.t(stage.name)}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Container>
      <Outlet />
    </DiscoveryModeContext.Provider>
  )
}